// ====================================================
// src/components/block/liveStreamManagement/enchainements/TbarSettingsModal.tsx
//
// Réglages du T-bar (DeckV2) : vitesse de la rampe AUTO et enchaînement actif
// (cf. registry.ts). Ouverte par le petit bouton engrenage sous le T-bar, qui
// a remplacé le sélecteur d'enchaînement à part et le champ "Vit." — les deux
// vivent maintenant ici. Même gabarit que les autres petites modales
// (SettingsModal dans SourceConfigModal.tsx) : portail, fond flouté, Echap.
// ====================================================

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

import { ENCHAINEMENTS, getEnchainementById } from './registry';
import { MiniTransitionPreview } from './MiniTransitionPreview';

export interface TbarSettingsModalProps {
  speedSec: number;
  onSpeedChange: (v: number) => void;
  selectedEnchainementId: string;
  onSelectEnchainement: (id: string) => void;
  /** Config de l'enchaînement SÉLECTIONNÉ (ex. direction pour Poussé/Recouvrement). */
  enchainementConfig: unknown;
  onEnchainementConfigChange: (config: unknown) => void;
  onClose: () => void;
}

const TbarSettingsModal: React.FC<TbarSettingsModalProps> = ({
  speedSec, onSpeedChange, selectedEnchainementId, onSelectEnchainement,
  enchainementConfig, onEnchainementConfigChange, onClose,
}) => {
  const selectedEnchainement = getEnchainementById(selectedEnchainementId);
  const extraControls = selectedEnchainement.renderControls({
    config: enchainementConfig,
    onChange: onEnchainementConfigChange,
  });
  const [speedText, setSpeedText] = useState(String(speedSec));
  useEffect(() => { setSpeedText(String(speedSec)); }, [speedSec]);
  const commitSpeedText = () => {
    const n = parseFloat(speedText.replace(',', '.'));
    if (Number.isFinite(n) && n > 0) onSpeedChange(n);
    else setSpeedText(String(speedSec));
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10050,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 480,
        maxHeight: 'calc(100vh - 32px)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.55)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>Réglages T-bar</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', overflowX: 'hidden' }}>

          {/* ── Vitesse ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Vitesse (AUTO)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={speedText}
                onChange={e => setSpeedText(e.target.value)}
                onBlur={commitSpeedText}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                style={{
                  width: 72, height: 30,
                  background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6, color: '#e2e8f0',
                  fontSize: 13, fontFamily: 'monospace', textAlign: 'center', outline: 'none',
                  padding: 0, boxSizing: 'border-box',
                }}
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>secondes pour une course pleine</span>
            </div>
          </div>

          {/* ── Enchaînement ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Effet utilisé
            </div>
            {/* Grille 2×5 : 10 enchaînements, toujours des tuiles — même si leur nombre
                change un jour, `repeat(5, 1fr)` reflue toujours en rangées pleines. Un peu
                plus larges que hautes (aspectRatio 1.2) pour que "Recouvrement" & co. aient
                de la marge, pas collés au bord. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {ENCHAINEMENTS.map(e => {
                const active = e.id === selectedEnchainementId;
                return (
                  <div
                    key={e.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectEnchainement(e.id)}
                    onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSelectEnchainement(e.id); } }}
                    title={e.label}
                    style={{
                      position: 'relative', aspectRatio: '1.2', minWidth: 0,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                      padding: '4px 6px', borderRadius: 10,
                      background: active ? 'linear-gradient(135deg, #065f46, #10b981)' : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${active ? '#34d399' : '#334155'}`,
                      color: active ? '#d1fae5' : '#cbd5e1',
                      cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
                    }}
                  >
                    <span style={{ fontSize: 9, fontWeight: 600, textAlign: 'center', lineHeight: 1.15, overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>{e.label}</span>
                    {/* Rejoue le vrai render() de l'enchaînement sur deux sources "F"/"P"
                        synthétiques — la config affichée est celle qui s'appliquerait
                        réellement (en cours d'édition si actif, sinon ses défauts). */}
                    <MiniTransitionPreview enchainement={e} config={active ? enchainementConfig : e.defaultConfig} width={44} height={26} />
                  </div>
                );
              })}
            </div>

            {/* Réglages propres à l'enchaînement sélectionné (ex. direction pour Poussé/
                Recouvrement/Volet) — sous la grille, jamais imbriqués dans une tuile. */}
            {extraControls && (
              <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid #334155' }}>
                {extraControls}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default TbarSettingsModal;
